import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth.js'
import { initials, PLATFORM_LABEL } from '../lib/format.js'
import { ChangePasswordCard } from '../components/ChangePasswordCard.js'
import { TwoFactorCard } from '../components/TwoFactorCard.js'
import { Badge, Card, CopyButton, Drawer, DrawerSection, Field, KeyValue, Notice, PageHead, RowsSkeleton, SettingRow, Switch, Tabs, Ticks } from '../components/ui.js'
import { BellIcon, MailIcon, PhoneIcon, ShieldIcon, StoreIcon, UsersIcon } from '../components/icons.js'
import { api, ApiError } from '../lib/api.js'
import { useTeam, ROLE_LABEL, type TeamRole } from '../hooks/useTeam.js'
import { useAccount, type Account } from '../hooks/useAccount.js'

type Section = 'general' | 'profile' | 'team' | 'security' | 'notifications'

const SECTIONS: Array<{ key: Section; label: string; path: string }> = [
  { key: 'general', label: 'General', path: '/settings' },
  { key: 'profile', label: 'Profile', path: '/settings/profile' },
  { key: 'team', label: 'Team', path: '/settings/team' },
  { key: 'security', label: 'Security', path: '/settings/security' },
  { key: 'notifications', label: 'Notifications', path: '/settings/notifications' },
]

export function SettingsPage(): React.ReactNode {
  const { section: sectionParam } = useParams<{ section?: Section }>()
  const navigate = useNavigate()
  const { user, shop, shops } = useAuth()

  const section: Section = SECTIONS.some((entry) => entry.key === sectionParam) ? (sectionParam as Section) : 'general'

  return (
    <>
      <PageHead title="Settings" subtitle="Your account, your team and how this dashboard behaves." />

      <Card flush>
        <Tabs
          value={section}
          onChange={(key) => navigate(SECTIONS.find((entry) => entry.key === key)!.path)}
          tabs={SECTIONS.map((entry) => ({ key: entry.key, label: entry.label }))}
        />
      </Card>

      {section === 'general' && <GeneralSection shopCount={shops.length} />}
      {section === 'profile' && <ProfileSection />}
      {section === 'team' && <TeamSection ownerName={user?.name ?? 'You'} ownerEmail={user?.email ?? ''} />}
      {section === 'security' && <SecuritySection />}
      {section === 'notifications' && <NotificationsSection />}
    </>
  )
}

// ── General ─────────────────────────────────────────────────────────────────

function GeneralSection({ shopCount }: { shopCount: number }): React.ReactNode {
  const { shop, shops } = useAuth()

  return (
    <div className="grid grid--2">
      <BusinessCard />

      <Card title="Regional">
        <KeyValue rows={[{ label: 'Currency', value: shop?.currency ?? 'INR' }]} />
      </Card>

      <Card title={`Connected stores (${shopCount})`} flush>
        {shops.length === 0 ? (
          <p className="card__body muted">No stores connected to this account yet.</p>
        ) : (
          <ul className="list">
            {shops.map((store) => (
              <li key={store.id}>
                <span className="feature__icon" style={{ ['--tone' as string]: 'var(--brand)' }}>
                  <StoreIcon size={16} />
                </span>
                <span className="list__text">
                  <strong>{store.name ?? store.domain}</strong>
                  <small>
                    {PLATFORM_LABEL[store.platform] ?? store.platform} · {store.domain}
                  </small>
                </span>
                <Badge tone={store.uninstalledAt ? 'bad' : 'ok'}>{store.uninstalledAt ? 'Disconnected' : 'Connected'}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <PauseCard />
    </div>
  )
}

/**
 * Pausing every checkout. Stores do not go dark: WooCommerce and Shopify fall
 * back to their own checkout, and only Outfalak stops taking orders.
 */
function PauseCard(): React.ReactNode {
  const { account, savePause } = useAccount()
  const paused = savePause.isPending ? Boolean(savePause.variables) : Boolean(account.data?.checkoutsPausedAt)

  return (
    <Card title="Pause checkouts">
      <SettingRow
        title="Pause all checkouts"
        description={
          paused
            ? 'Paused. Shoppers are using your store\'s own checkout; Outfalak takes no orders until you resume.'
            : 'Hand every store back to its own checkout. Outfalak takes no new orders until you resume.'
        }
      >
        <Switch
          checked={paused}
          label="Pause all checkouts"
          disabled={!account.data?.canEdit || savePause.isPending}
          onChange={(next) => {
            if (next && !window.confirm('Pause Outfalak checkout on every store? Shoppers will use your store\'s own checkout.')) return
            savePause.mutate(next)
          }}
        />
      </SettingRow>
      {paused && (
        <Notice kind="warn">
          Custom websites have no checkout of their own to fall back to, so they cannot take orders while paused. WooCommerce
          stores need plugin 2.5.10 or later to hide the button.
        </Notice>
      )}
      {savePause.error && <Notice kind="error">{(savePause.error as Error).message}</Notice>}
      {account.data && !account.data.canEdit && <Notice>Only an owner can change this.</Notice>}
    </Card>
  )
}

/** Loads the account, then keys the form on it so a save refills the fields. */
function BusinessCard(): React.ReactNode {
  const { account } = useAccount()

  return (
    <Card title="Business" subtitle="Shown on your commission statements.">
      {account.isPending && <RowsSkeleton rows={3} />}
      {account.error && <Notice kind="error">{(account.error as Error).message}</Notice>}
      {account.data && <BusinessForm key={`${account.data.name}|${account.data.gstin}`} account={account.data} />}
    </Card>
  )
}

function BusinessForm({ account }: { account: Account }): React.ReactNode {
  const { saveBusiness } = useAccount()
  const [name, setName] = useState(account.name)
  const [gstin, setGstin] = useState(account.gstin ?? '')

  const cleaned = gstin.trim().toUpperCase()
  const dirty = name.trim() !== account.name || cleaned !== (account.gstin ?? '')

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault()
        saveBusiness.mutate({ name: name.trim(), gstin: cleaned || null })
      }}
    >
      <Field label="Business name">
        <input className="input" value={name} maxLength={190} disabled={!account.canEdit} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="GSTIN" hint="Optional. 15 characters, for example 22AAAAA0000A1Z5.">
        <input
          className="input"
          value={gstin}
          maxLength={15}
          autoCapitalize="characters"
          disabled={!account.canEdit}
          onChange={(e) => setGstin(e.target.value)}
        />
      </Field>

      {saveBusiness.error && <Notice kind="error">{(saveBusiness.error as Error).message}</Notice>}
      {saveBusiness.isSuccess && !dirty && <Notice kind="ok">Saved.</Notice>}
      {!account.canEdit && <Notice>Only an owner can change these.</Notice>}

      <button type="submit" className="btn btn--primary" disabled={!account.canEdit || !dirty || !name.trim() || saveBusiness.isPending}>
        {saveBusiness.isPending ? 'Saving…' : 'Save changes'}
      </button>
    </form>
  )
}

// ── Profile ─────────────────────────────────────────────────────────────────

function ProfileSection(): React.ReactNode {
  const { user, shop } = useAuth()
  const name = user?.name ?? shop?.name ?? 'Account'

  return (
    <div className="grid grid--2">
      <Card title="Your profile">
        <div className="row" style={{ marginBottom: 20 }}>
          <span className="avatar avatar--lg">{initials(name)}</span>
          <span className="list__text">
            <strong>{name}</strong>
            <small>{user?.email ?? shop?.domain ?? ''}</small>
          </span>
        </div>

        {user && <ProfileForm key={user.name} name={user.name} email={user.email} phone={user.phone} />}
      </Card>

      <Card title="Account">
        <dl className="kv">
          <div className="kv__row">
            <dt>Role</dt>
            <dd>Merchant owner</dd>
          </div>
          <div className="kv__row">
            <dt>Email verified</dt>
            <dd>{user?.email ? <Badge tone="ok">Verified</Badge> : <Badge tone="warn">Not set</Badge>}</dd>
          </div>
          <div className="kv__row">
            <dt>Onboarded</dt>
            <dd>{user?.onboardedAt ? <Badge tone="ok">Complete</Badge> : <Badge tone="warn">In progress</Badge>}</dd>
          </div>
          <div className="kv__row">
            <dt>Merchant ID</dt>
            <dd>
              <code>{user?.merchantId ?? '—'}</code>
            </dd>
          </div>
        </dl>
      </Card>
    </div>
  )
}

/** Email and phone are how this account signs in, so they are shown, not edited. */
function ProfileForm({ name, email, phone }: { name: string; email: string | null; phone: string | null }): React.ReactNode {
  const { refresh } = useAuth()
  const [next, setNext] = useState(name)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  async function save(): Promise<void> {
    setSaving(true)
    setFailed(null)

    try {
      await api('/v1/account/profile', { method: 'PUT', body: { name: next.trim() } })
      await refresh()
    } catch (cause) {
      setFailed((cause as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault()
        void save()
      }}
    >
      <Field label="Full name">
        <input className="input" value={next} maxLength={255} onChange={(e) => setNext(e.target.value)} />
      </Field>
      <Field label="Email" hint="Used to sign in. Contact support to change it.">
        <div className="input-group">
          <MailIcon size={16} />
          <input className="input" type="email" value={email ?? ''} readOnly />
        </div>
      </Field>
      <Field label="Phone" hint="Used to sign in. Contact support to change it.">
        <div className="input-group">
          <PhoneIcon size={16} />
          <input className="input" inputMode="tel" value={phone ?? ''} readOnly />
        </div>
      </Field>

      {failed && <Notice kind="error">{failed}</Notice>}

      <button type="submit" className="btn btn--primary" disabled={!next.trim() || next.trim() === name || saving}>
        {saving ? 'Saving…' : 'Save profile'}
      </button>
    </form>
  )
}

// ── Team ────────────────────────────────────────────────────────────────────

function TeamSection({ ownerName, ownerEmail }: { ownerName: string; ownerEmail: string }): React.ReactNode {
  const { team, loading, error, invite, revoke, setRole, remove } = useTeam()

  const [inviting, setInviting] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setNewRole] = useState<TeamRole>('staff')
  /**
   * The created link, held until the owner closes the panel.
   *
   * The API returns it once and stores only a hash, so this is the only moment
   * it exists anywhere. Closing the panel on success, as a form normally would,
   * would destroy the one thing the owner came here for.
   */
  const [link, setLink] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  function close(): void {
    setInviting(false)
    setName('')
    setEmail('')
    setNewRole('staff')
    setLink(null)
    setFailed(null)
  }

  async function submit(): Promise<void> {
    setFailed(null)

    try {
      const created = await invite.mutateAsync({
        role,
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
      })

      setLink(created.url)
    } catch (cause) {
      setFailed(cause instanceof ApiError ? cause.message : 'That invite could not be created.')
    }
  }

  const members = team?.members ?? []
  const invites = team?.invites ?? []
  const owners = members.filter((member) => member.role === 'owner').length

  return (
    <>
      {error && (
        <Notice kind="warn" title="Could not load the team">
          {error instanceof ApiError ? error.message : 'Something went wrong reaching the server.'}
        </Notice>
      )}

      <Card
        title="Team members"
        action={
          <button type="button" className="btn btn--primary btn--sm" onClick={() => setInviting(true)}>
            Invite member
          </button>
        }
        flush
      >
        {loading ? (
          <RowsSkeleton rows={2} />
        ) : (
          <ul className="list">
            {members.length === 0 && (
              <li>
                <span className="avatar avatar--sm">{initials(ownerName)}</span>
                <span className="list__text">
                  <strong>{ownerName}</strong>
                  <small>{ownerEmail}</small>
                </span>
                <Badge tone="brand">Owner</Badge>
              </li>
            )}

            {members.map((member) => (
              <li key={member.id}>
                <span className="avatar avatar--sm">{initials(member.name)}</span>
                <span className="list__text">
                  <strong>
                    {member.name}
                    {member.isYou && <small style={{ marginLeft: 8 }}>You</small>}
                  </strong>
                  <small>{member.email ?? member.phone ?? '—'}</small>
                </span>

                <select
                  className="input input--sm"
                  value={member.role}
                  aria-label={`Role for ${member.name}`}
                  /*
                   * Disabled on the last owner rather than allowed and refused.
                   * The API enforces it either way; letting the select move and
                   * then snapping back is a worse way to learn the rule.
                   */
                  disabled={member.role === 'owner' && owners <= 1}
                  onChange={(event) => setRole.mutate({ id: member.id, role: event.target.value as TeamRole })}
                >
                  <option value="owner">{ROLE_LABEL.owner}</option>
                  <option value="staff">{ROLE_LABEL.staff}</option>
                  <option value="read_only">{ROLE_LABEL.read_only}</option>
                </select>

                {!member.isYou && !(member.role === 'owner' && owners <= 1) && (
                  <button
                    type="button"
                    className="btn btn--sm"
                    onClick={() => remove.mutate(member.id)}
                    disabled={remove.isPending}
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {invites.length > 0 && (
        <Card title="Pending invites" flush>
          <ul className="list">
            {invites.map((pending) => (
              <li key={pending.id}>
                <span className="avatar avatar--sm">{initials(pending.name ?? pending.email ?? '?')}</span>
                <span className="list__text">
                  <strong>{pending.name ?? pending.email ?? 'Invite link'}</strong>
                  <small>
                    {ROLE_LABEL[pending.role]}
                    {pending.expired ? ' — expired' : ` — expires ${new Date(pending.expiresAt).toLocaleDateString()}`}
                  </small>
                </span>
                {pending.expired ? <Badge tone="warn">Expired</Badge> : <Badge tone="info">Invited</Badge>}
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={() => revoke.mutate(pending.id)}
                  disabled={revoke.isPending}
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {inviting && (
        <Drawer open title="Invite a teammate" onClose={close}>
          {link ? (
            <DrawerSection title="Send them this link">
              <Notice kind="info" title="Shown once">
                Nothing stores this link — only a hash of it. Copy it now; if it is lost, revoke the invite and make a
                new one.
              </Notice>

              <Field label="Invite link" hint="Single use, and it expires in 14 days.">
                <input className="input" readOnly value={link} onFocus={(event) => event.target.select()} />
              </Field>

              <CopyButton value={link} label="Copy invite link" />

              <div className="row" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn--primary" onClick={close}>
                  Done
                </button>
              </div>
            </DrawerSection>
          ) : (
            <DrawerSection title="Who is joining">
              {failed && (
                <Notice kind="warn" title="That did not work">
                  {failed}
                </Notice>
              )}

              <Field label="Name" hint="Optional. It makes the pending row readable.">
                <input className="input" value={name} onChange={(event) => setName(event.target.value)} />
              </Field>

              <Field label="Email" hint="Optional, and not sent anywhere — you share the link yourself.">
                <input
                  className="input"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="them@example.com"
                />
              </Field>

              <Field label="Role">
                <select className="input" value={role} onChange={(event) => setNewRole(event.target.value as TeamRole)}>
                  <option value="staff">Staff — orders, customers and checkout settings</option>
                  <option value="read_only">Read only — can see everything, change nothing</option>
                  <option value="owner">Owner — everything, including billing</option>
                </select>
              </Field>

              <div className="row" style={{ marginTop: 16, gap: 8 }}>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => void submit()}
                  disabled={invite.isPending}
                >
                  {invite.isPending ? 'Creating…' : 'Create invite link'}
                </button>
                <button type="button" className="btn" onClick={close}>
                  Cancel
                </button>
              </div>
            </DrawerSection>
          )}
        </Drawer>
      )}

      <Card title="Roles">
        <ul className="list list--plain">
          <li style={{ paddingLeft: 0, alignItems: 'flex-start' }}>
            <span className="feature__icon" style={{ ['--tone' as string]: 'var(--brand)' }}>
              <UsersIcon size={16} />
            </span>
            <span className="list__text">
              <strong>Owner</strong>
              <small>Everything, including billing and deleting the account.</small>
            </span>
          </li>
          <li style={{ paddingLeft: 0, alignItems: 'flex-start' }}>
            <span className="feature__icon" style={{ ['--tone' as string]: 'var(--info)' }}>
              <UsersIcon size={16} />
            </span>
            <span className="list__text">
              <strong>Staff</strong>
              <small>Orders, customers and checkout settings. No billing.</small>
            </span>
          </li>
          <li style={{ paddingLeft: 0, alignItems: 'flex-start' }}>
            <span className="feature__icon" style={{ ['--tone' as string]: 'var(--ink-faint)' }}>
              <UsersIcon size={16} />
            </span>
            <span className="list__text">
              <strong>Read only</strong>
              <small>Can see everything, can change nothing.</small>
            </span>
          </li>
        </ul>
      </Card>
    </>
  )
}

// ── Security ────────────────────────────────────────────────────────────────

function SecuritySection(): React.ReactNode {
  const { user, signOutEverywhere } = useAuth()
  const [failed, setFailed] = useState<string | null>(null)

  return (
    <div className="grid grid--2">
      {/* Inside Shopify there is no account of ours: no password, and Shopify owns the session. */}
      {user && <ChangePasswordCard />}

      <div className="stack">
        {user && (
          <Card title="Sessions">
            <SettingRow title="This device" description="Signed in now">
              <Badge tone="ok" dot>
                Active
              </Badge>
            </SettingRow>

            <SettingRow title="Sign out everywhere" description="Revokes every session on every device, including this one.">
              <button
                type="button"
                className="btn btn--danger btn--sm"
                onClick={() => {
                  setFailed(null)
                  signOutEverywhere().catch((error: Error) => setFailed(error.message))
                }}
              >
                Sign out everywhere
              </button>
            </SettingRow>
            {failed && <Notice kind="error">{failed}</Notice>}
          </Card>
        )}

        {user && <TwoFactorCard />}

        <Card title="Good practice" accent>
          <Ticks
            brand
            items={[
              'Use a unique password for this account',
              'Never share API secret keys over chat or email',
              'Rotate keys when someone leaves your team',
            ]}
          />
        </Card>
      </div>
    </div>
  )
}

// ── Notifications ───────────────────────────────────────────────────────────

/**
 * The alerts this platform actually sends a merchant: the low wallet balance
 * warning. Each switch saves on change. Other events reach the bell in the top
 * bar and have no email or WhatsApp delivery to turn on.
 */
function NotificationsSection(): React.ReactNode {
  const { account, saveAlerts } = useAccount()
  const alerts = saveAlerts.isPending ? saveAlerts.variables : account.data?.walletAlerts
  const disabled = !account.data?.canEdit || saveAlerts.isPending

  return (
    <div className="grid grid--2">
      <Card title="Low wallet balance" subtitle="Sent before a low balance starts failing orders.">
        {account.isPending && <RowsSkeleton rows={2} />}
        {account.error && <Notice kind="error">{(account.error as Error).message}</Notice>}

        {alerts && (
          <>
            <SettingRow title="Email" description="Sent to your merchant email address.">
              <Switch
                checked={alerts.email}
                label="Email"
                disabled={disabled}
                onChange={(email) => saveAlerts.mutate({ ...alerts, email })}
              />
            </SettingRow>
            <SettingRow title="WhatsApp" description="Sent to your merchant mobile number.">
              <Switch
                checked={alerts.whatsapp}
                label="WhatsApp"
                disabled={disabled}
                onChange={(whatsapp) => saveAlerts.mutate({ ...alerts, whatsapp })}
              />
            </SettingRow>
          </>
        )}

        {saveAlerts.error && <Notice kind="error">{(saveAlerts.error as Error).message}</Notice>}
        {account.data && !account.data.canEdit && <Notice>Only an owner can change these.</Notice>}
      </Card>

      <Card title="In the dashboard">
        <p className="muted" style={{ fontSize: '.87rem' }}>
          <BellIcon size={13} style={{ verticalAlign: '-2px' }} /> Payments, refunds and wallet alerts always appear in the bell in
          the top bar.
        </p>
      </Card>
    </div>
  )
}
